import type { Recurrence, RecurrenceUnit } from '../endpoints/types';
import type { NodeInstanceScheduleSnapshot } from './buildScheduleSnapshot';

type ForwardedRecurrence = Omit<Recurrence, 'unit' | 'interval'>;

/** Flat single-rule schedule held in submit form state before a definition is applied. */
export type ScheduleData = {
  unit: RecurrenceUnit | null;
  interval: number | null;
} & {
  // Required presence (value may be undefined), not optional: forgetting to forward
  // a field becomes a compile error instead of a silent drop (#856).
  [K in keyof ForwardedRecurrence]-?: ForwardedRecurrence[K] | undefined;
};

/** Submit body schedule: either legacy flat form state or a multi-rule snapshot. */
export type SubmitSchedule = ScheduleData | NodeInstanceScheduleSnapshot;

/** Legacy node_instances.schedule shape with top-level unit/interval (pre-multi-recurrence). */
export type LegacyFlatSchedule = Pick<Recurrence, 'unit' | 'interval'> &
  Partial<Omit<Recurrence, 'unit' | 'interval'>> & {
    readonly resubmit_on_error?: boolean;
  };

export const isNodeInstanceScheduleSnapshot = (schedule: SubmitSchedule): schedule is NodeInstanceScheduleSnapshot =>
  'recurrences' in schedule && Array.isArray(schedule.recurrences);

export const isNodeInstanceScheduleSnapshotValue = (value: unknown): value is NodeInstanceScheduleSnapshot => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const schedule = value as NodeInstanceScheduleSnapshot;
  return Array.isArray(schedule.recurrences) && schedule.recurrences.length > 0;
};

export const isLegacyFlatSchedule = (value: unknown): value is LegacyFlatSchedule => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const schedule = value as LegacyFlatSchedule;
  return typeof schedule.unit === 'string' && schedule.interval != null;
};

/** True when schedule is either a multi-rule snapshot or a legacy flat recurrence. */
export const isScheduleSnapshot = (value: unknown): boolean => isNodeInstanceScheduleSnapshotValue(value) || isLegacyFlatSchedule(value);
